import React, { Fragment } from "react";
import { withTheme, withStyles } from "@material-ui/core/styles";
import { injectIntl } from "react-intl";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Tooltip,
  TextField,
  Button,
  Box,
  TextareaAutosize,
  Paper,
} from "@material-ui/core";
import { withHistory, formatMessage, FormPanel } from "@openimis/fe-core";
import { withModulesManager, journalize, formatDateFromISO } from "@openimis/fe-core";
import { DETAIL_IN_REVIEW, DETAIL_DATA_COLLECTION_IN_PROGRESS, DETAIL_FORM_DEPLOYED } from "../constants";
import StatePicker from "../pickers/StatePicker";
import { approveOrQADetail, fetchQADetailForms } from "../actions";

const styles = (theme) => ({
  tableTitle: theme.table.title,
  page: theme.page,
  paper: theme.paper.paper,
  item: theme.paper.item,
  title: theme.paper.title,
  fullHeight: {
    height: "100%",
  },
  commentBox: {
    width: "100%",
    minWidth: 400,
    minHeight: 120,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontFamily: theme.typography.fontFamily,
    fontSize: "1rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    resize: "vertical",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  actionButton: {
    minWidth: 100,
  },
});

class QualityAssuranceMasterPanel extends FormPanel {
  state = {
    rejectModalOpen: false,
    rejectionComment: "",
    isProcessing: false,
    isApproved: false,
    validatedForms: [],
    forms: [],
    validating: false,
    validationSuccess: false,
    validationError: null,
    selectedForm: null, // in case you need to track form being validated
    hasValidatedForms: false,
  };

  componentDidMount() {
    this.checkValidatedForms();
  }

  // Refetch forms or recalculate when edited or form data changes

  componentDidUpdate(prevProps) {
    if (prevProps.qaDetailsForms !== this.props.qaDetailsForms || prevProps.edited !== this.props.edited) {
      const { qaDetailsForms } = this.props;
      this.setState({ forms: qaDetailsForms || [] }, this.checkValidatedForms);
    }
  }

  // Validates if all forms related to current quality_assurance_and_accreditation have submitted data
  checkValidatedForms = () => {
    const { edited, validatedForms } = this.props;
    const { forms } = this.state;
    if (!edited || !edited) return;
    const qualityAssuranceId = edited?.id;

    // Append quality_assurance_and_accreditationId to each form for reference
    const publishedForms = forms.map((form) => ({
      ...form,
      qualityAssuranceId,
    }));

    // Only allow approval if all forms have `submitedData === true`
    const hasValidatedForms = publishedForms.length > 0 && publishedForms.every((form) => form.submitedData === true);

    this.setState({ hasValidatedForms });
  };
  handleApproveClick = () => {
    this.handleApproveOrReject("approve", "Approved QA Detail");
  };

  handleFailedClick = () => {
    this.handleApproveOrReject("failed", "Rejected QA Detail");
    this.closeRejectModal();
  };
  openRejectModal = () => this.setState({ rejectModalOpen: true });
  closeRejectModal = () => this.setState({ rejectModalOpen: false, rejectionComment: "" });
  handleCommentChange = (e) => this.setState({ rejectionComment: e.target.value });

  // Handles approval or rejection of an quality_assurance_and_accreditation
  handleApproveOrReject = (action, comments) => {
    const { edited, approveOrQADetail } = this.props;
    if (!edited || !edited) return;

    this.setState({ isProcessing: true });

    let status,
      declineType = "";
    if (action === "approve") {
      status = "AP";
      this.setState({ isApproved: true });
    } else if (action === "probation") {
      status = "DC";
      declineType = "probation";
    } else if (action === "failed") {
      status = "DC";
      declineType = "failed";
    }

    const payload = {
      uuids: [edited?.uuid],
      status: status,
      declineType,
      declineReason: "",
      comments: comments || "",
      clientMutationLabel: `${action === "approve" ? "Approved" : "Rejected"} QA Detail`,
    };

    approveOrQADetail(payload)
      .then(() => setTimeout(() => window.location.reload(), 1500))
      .catch((error) => {
        console.error("Approval error:", error);
        this.setState({ isProcessing: false });
      });
  };

  render() {
    const { classes, updateAttribute, edited, initiateQADetail, validatedForms, startOrStopQA } = this.props;

    const handleEvent = () => {
      window.location.reload();
    };

    const { rejectModalOpen, rejectionComment, isProcessing, isApproved, forms } = this.state;

    const isAlreadyApproved = edited && edited?.status === "AP";
    const showValidTill = isApproved || isAlreadyApproved;
    const canApprove = !isAlreadyApproved && this.state.hasValidatedForms && !isProcessing;

    return (
      <Fragment>
        <Paper className={classes.paper}>
          <Typography className={classes.title}>Quality Assurance Details</Typography>
          <div className={classes.page}>
            {/* form content unchanged... */}
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Quality Assurance Name"
                  value={edited ? edited?.projectName : ""}
                  onChange={(e) => updateAttribute("projectName", e.target.value)}
                  disabled={edited}
                />
              </Grid>
              <Grid item xs={4}>
                <StatePicker value={edited ? edited.state : null} readOnly={edited} />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  value={
                    !!edited ? formatDateFromISO(this.props.modulesManager, this.props.intl, edited?.createdDate) : ""
                  }
                  label="Date Started"
                  disabled={edited}
                />
              </Grid>
              {showValidTill && (
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Valid Till"
                    value={edited && edited[0]?.created ? this.calculateValidTill(edited[0].created) : ""}
                    disabled
                  />
                </Grid>
              )}
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  value={
                    !!edited
                      ? formatMessage(
                          this.props.intl,
                          "quality_assurance_and_accreditation",
                          `quality_assurance_and_accreditation.status.${edited?.status}`,
                        )
                      : ""
                  }
                  label="Status"
                  disabled={edited}
                />
              </Grid>
            </Grid>
            {edited && (
              <Box display="flex" justifyContent="end" mt={2}>
                {edited.state === DETAIL_FORM_DEPLOYED && (
                  <Box mx={2}>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        startOrStopQA("start");
                        handleEvent();
                      }}
                    >
                      Start Quality Assurance
                    </Button>
                  </Box>
                )}
                {edited && !edited?.isInitialized && (
                  <Box display="flex" justifyContent="end" mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        initiateQADetail();
                        handleEvent();
                      }}
                    >
                      Initiate Quality Assurance
                    </Button>
                  </Box>
                )}

                {edited.state === DETAIL_DATA_COLLECTION_IN_PROGRESS && (
                  <Box>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        startOrStopQA("stop");
                        handleEvent();
                      }}
                    >
                      Stop Quality Assurance
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            {/* Approval or rejection actions (only in review state) */}
            {edited && edited?.state === DETAIL_IN_REVIEW && !isAlreadyApproved && (
              <Box display="flex" justifyContent="end" mt={2}>
                <Box mx={2}>
                  <Tooltip
                    title={!this.state.hasValidatedForms ? "You must validate all forms before approving" : ""}
                    placement="top"
                  >
                    <span>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={this.handleApproveClick}
                        disabled={!canApprove}
                      >
                        {isProcessing ? "Processing..." : "Approve"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Box>
                  <Button color="error" variant="outlined" onClick={this.openRejectModal} disabled={isProcessing}>
                    Reject
                  </Button>
                </Box>
              </Box>
            )}
          </div>
        </Paper>

        {/* Modal for entering rejection reason */}
        <Dialog open={rejectModalOpen} onClose={this.closeRejectModal} maxWidth="sm">
          <DialogTitle>Reject Quality Assurance</DialogTitle>
          <DialogContent className={classes.rejectDialog}>
            <Typography variant="body1">Please provide a reason for rejection:</Typography>
            <TextareaAutosize
              className={classes.commentBox}
              minRows={4}
              placeholder="Enter comment here..."
              value={rejectionComment}
              onChange={this.handleCommentChange}
              disabled={isProcessing}
            />
          </DialogContent>
          <DialogActions className={classes.buttonContainer}>
            <Button variant="contained" color="primary" onClick={this.handleProbationClick} disabled={isProcessing}>
              {isProcessing ? "Processing..." : "Probation"}
            </Button>
            <Button variant="contained" color="primary" onClick={this.handleFailedClick} disabled={isProcessing}>
              {isProcessing ? "Processing..." : "Failed"}
            </Button>
          </DialogActions>
        </Dialog>
      </Fragment>
    );
  }
}

// Redux mappings to props
const mapStateToProps = (state) => ({
  rights: !!state.core && !!state.core.user && !!state.core.user.i_user ? state.core.user.i_user.rights : [],
  mutation: state.core.mutation,
  validatedForms: state.quality_assurance_and_accreditation.validatedForms,
  fetchedAllForms: state.quality_assurance_and_accreditation.fetchedAllForms || [],
  allForms: state.quality_assurance_and_accreditation.allForms || [],
  forms: state.quality_assurance_and_accreditation.forms || [],
  quality_assurance_and_accreditationDetailsForms:
    state.quality_assurance_and_accreditation.quality_assurance_and_accreditationDetailsForms,
  fetchingAccreditationDetailsForms: state.quality_assurance_and_accreditation.fetchingAccreditationDetailsForms,
  fetchedAccreditationDetailsForms: state.quality_assurance_and_accreditation.fetchedAccreditationDetailsForms,
  errorAccreditationDetailsForms: state.quality_assurance_and_accreditation.errorAccreditationDetailsForms,
  qaDetailsForms: state.quality_assurance_and_accreditation.qaDetailsForms,
  fetchingQADetailsForms: state.quality_assurance_and_accreditation.fetchingQADetailsForms,
  fetchedQADetailsForms: state.quality_assurance_and_accreditation.fetchedQADetailsForms,
  errorQADetailsForms: state.quality_assurance_and_accreditation.errorQADetailsForms,
  quality_assurance_and_accreditationDetailsFormData:
    state.quality_assurance_and_accreditation.quality_assurance_and_accreditationDetailsFormData,
  qaDetailsFormData: state.quality_assurance_and_accreditation.qaDetailsFormData,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ journalize, approveOrQADetail, fetchQADetailForms }, dispatch);

// Exporting wrapped component with HOCs
export default withModulesManager(
  injectIntl(
    withTheme(
      withStyles(styles)(withHistory(connect(mapStateToProps, mapDispatchToProps)(QualityAssuranceMasterPanel))),
    ),
  ),
);
